import * as z from 'zod';

// Shared primitives for the Noor Eats **merchant** API — mirrors the reusable
// `v1*` component schemas in `apps/merchant-panel/openapi.json`. Every service
// module in this slice builds on these: the protobuf-style string enums, the
// pagination shape, geo/address value objects and the list-envelope helper.
//
// Conventions, and where the wire deviates from the spec (verified against
// `test.api.noor.uz`):
//
// - The backend speaks protobuf-JSON: enums are SCREAMING_SNAKE strings with an
//   `*_UNSPECIFIED` zero value; ids are UUID strings; `int64` money/counts
//   arrive as JSON numbers. Wire values are kept verbatim — mapping to UI
//   labels is a `lib/` concern — but every enum `.catch(...)`es to its
//   `*_UNSPECIFIED` member so a novel server value degrades gracefully instead
//   of failing the whole payload.
// - Timestamps are NAIVE (`2026-07-21T10:43:47` — no `Z`, no offset), so they
//   are modelled as plain strings. `z.iso.datetime()` would reject them.
// - Objects are non-strict on purpose: the wire carries undocumented keys (e.g.
//   `optional_merchant_id`, a protobuf oneof-name artifact) that must not fail
//   a parse.

/** A naive-or-zoned backend timestamp. Not `z.iso.datetime()` — see above. */
export const timestampSchema = z.string();

/**
 * A `uint64` amount. protobuf-JSON may send 64-bit integers as numbers or as
 * decimal strings (`"10000"`), so both are accepted and normalised to a number;
 * absent, `null` or unparseable collapses to `0` (proto's own zero value).
 */
export const uint64 = z
  .union([z.number(), z.string()])
  .nullish()
  .transform((value) => {
    const n = typeof value === 'string' ? Number(value) : (value ?? 0);
    return Number.isFinite(n) && n > 0 ? n : 0;
  });

/**
 * An optional field the wire may send as an explicit `null`.
 *
 * protobuf-JSON omits a message field at its zero value, but the gateway
 * serialises unset `google.protobuf.Timestamp`s and unset sub-messages as
 * `null` instead (`ready_at: null`, `courier: null` on an order that has not
 * reached that stage). Plain `.optional()` accepts only `undefined` and would
 * fail the whole payload, so every nullable field goes through this: `null` is
 * normalised to `undefined`, leaving one absent-value shape for the UI.
 */
export const optional = <T extends z.ZodType>(schema: T) =>
  schema
    .nullish()
    .transform((value) => value ?? undefined)
    .optional();

// ── Enums ────────────────────────────────────────────────────────────────────

/** Order lifecycle (`v1OrderStatus`). */
export const orderStatusSchema = z
  .enum([
    'ORDER_STATUS_UNSPECIFIED',
    'ORDER_STATUS_PENDING',
    'ORDER_STATUS_CONFIRMED',
    'ORDER_STATUS_PAID',
    'ORDER_STATUS_DELIVERED',
    'ORDER_STATUS_CANCELLED',
    'ORDER_STATUS_AWAITING_PAYMENT',
  ])
  .catch('ORDER_STATUS_UNSPECIFIED');
export type OrderStatus = z.infer<typeof orderStatusSchema>;

/** Merchant order status lifecycle (`v1MerchantOrderStatus`). */
export const merchantOrderStatusSchema = z
  .enum([
    'MERCHANT_ORDER_STATUS_UNSPECIFIED',
    'MERCHANT_ORDER_STATUS_NEW',
    'MERCHANT_ORDER_STATUS_PREPARING',
    'MERCHANT_ORDER_STATUS_READY',
    'MERCHANT_ORDER_STATUS_PICKED_UP',
    'MERCHANT_ORDER_STATUS_DELIVERED',
    'MERCHANT_ORDER_STATUS_CANCELLED',
  ])
  .catch('MERCHANT_ORDER_STATUS_UNSPECIFIED');
export type MerchantOrderStatus = z.infer<typeof merchantOrderStatusSchema>;

/** Filter parameter for `GET /api/v1/merchant/orders/by-restaurant`. */
export const merchantOrderFilterSchema = z
  .enum([
    'MERCHANT_ORDER_FILTER_UNSPECIFIED',
    'MERCHANT_ORDER_FILTER_NEW',
    'MERCHANT_ORDER_FILTER_PREPARING',
    'MERCHANT_ORDER_FILTER_COMPLETED',
  ])
  .catch('MERCHANT_ORDER_FILTER_UNSPECIFIED');
export type MerchantOrderFilter = z.infer<typeof merchantOrderFilterSchema>;

/** Product availability (`v1ProductStatus`). */
export const productStatusSchema = z
  .enum([
    'PRODUCT_STATUS_UNSPECIFIED',
    'PRODUCT_STATUS_AVAILABLE',
    'PRODUCT_STATUS_STOP_LIST',
  ])
  .catch('PRODUCT_STATUS_UNSPECIFIED');
export type ProductStatus = z.infer<typeof productStatusSchema>;

/**
 * Why a dish can't be ordered right now (client reads). Normalised to the bare
 * name — `OUT_OF_SCHEDULE` (outside its selling hours), `OUT_OF_STOCK`,
 * `DISABLED` (switched off by the merchant) — or `UNSPECIFIED` (all fine). The
 * wire value is matched by its trailing token, so a prefixed protobuf enum
 * name (`..._OUT_OF_STOCK`) and the bare one both resolve, and anything
 * unknown reads as orderable rather than hiding a dish on a value we can't
 * interpret.
 */
export const PRODUCT_UNAVAILABLE_REASONS = [
  'OUT_OF_SCHEDULE',
  'OUT_OF_STOCK',
  'DISABLED',
  'UNSPECIFIED',
] as const;
export type ProductUnavailableReason = (typeof PRODUCT_UNAVAILABLE_REASONS)[number];
export const productUnavailableReasonSchema = z
  .string()
  .nullish()
  .transform((value): ProductUnavailableReason => {
    const normalized = (value ?? '').toUpperCase();
    return (
      PRODUCT_UNAVAILABLE_REASONS.find(
        (reason) => reason !== 'UNSPECIFIED' && normalized.endsWith(reason),
      ) ?? 'UNSPECIFIED'
    );
  });

/** Restaurant visibility/open state (`v1RestaurantStatus`). */
export const restaurantStatusSchema = z
  .enum([
    'RESTAURANT_STATUS_UNSPECIFIED',
    'RESTAURANT_STATUS_HIDDEN',
    'RESTAURANT_STATUS_OPEN',
    'RESTAURANT_STATUS_CLOSED',
  ])
  .catch('RESTAURANT_STATUS_UNSPECIFIED');
export type RestaurantStatus = z.infer<typeof restaurantStatusSchema>;

/** Branch moderation state returned on `v1Restaurant.moderation_state`. */
export const merchantModerationStateSchema = z
  .enum([
    'MERCHANT_MODERATION_STATE_UNSPECIFIED',
    'MERCHANT_MODERATION_STATE_DRAFT',
    'MERCHANT_MODERATION_STATE_ON_MODERATION',
    'MERCHANT_MODERATION_STATE_APPROVED',
    'MERCHANT_MODERATION_STATE_REJECTED',
  ])
  .catch('MERCHANT_MODERATION_STATE_UNSPECIFIED');
export type MerchantModerationState = z.infer<typeof merchantModerationStateSchema>;

/** Product moderation state returned on merchant product reads. */
export const productModerationStateSchema = z
  .enum([
    'PRODUCT_MODERATION_STATE_UNSPECIFIED',
    'PRODUCT_MODERATION_STATE_DRAFT',
    'PRODUCT_MODERATION_STATE_ON_MODERATION',
    'PRODUCT_MODERATION_STATE_APPROVED',
    'PRODUCT_MODERATION_STATE_REJECTED',
    // The backend also emits the merchant-scoped spelling on product reads.
    'MERCHANT_MODERATION_STATE_UNSPECIFIED',
    'MERCHANT_MODERATION_STATE_DRAFT',
    'MERCHANT_MODERATION_STATE_ON_MODERATION',
    'MERCHANT_MODERATION_STATE_APPROVED',
    'MERCHANT_MODERATION_STATE_REJECTED',
  ])
  .catch('PRODUCT_MODERATION_STATE_UNSPECIFIED');
export type ProductModerationState = z.infer<typeof productModerationStateSchema>;

export const paymentTypeSchema = z
  .enum(['PAYMENT_TYPE_UNSPECIFIED', 'PAYMENT_TYPE_CASH', 'PAYMENT_TYPE_CARD'])
  .catch('PAYMENT_TYPE_UNSPECIFIED');
export type PaymentType = z.infer<typeof paymentTypeSchema>;

export const deliveryMethodSchema = z
  .enum([
    'DELIVERY_METHOD_UNSPECIFIED',
    'DELIVERY_METHOD_DELIVERY',
    'DELIVERY_METHOD_PICKUP',
  ])
  .catch('DELIVERY_METHOD_UNSPECIFIED');
export type DeliveryMethod = z.infer<typeof deliveryMethodSchema>;

export const promoTypeSchema = z
  .enum(['PROMO_TYPE_UNSPECIFIED', 'PROMO_TYPE_DISCOUNT', 'PROMO_TYPE_BUY_X_GET_Y'])
  .catch('PROMO_TYPE_UNSPECIFIED');
export type PromoType = z.infer<typeof promoTypeSchema>;

export const discountTypeSchema = z
  .enum(['DISCOUNT_TYPE_UNSPECIFIED', 'DISCOUNT_TYPE_PERCENTAGE', 'DISCOUNT_TYPE_FIXED'])
  .catch('DISCOUNT_TYPE_UNSPECIFIED');
export type DiscountType = z.infer<typeof discountTypeSchema>;

export const measurementTypeSchema = z
  .enum([
    'MEASUREMENT_TYPE_UNSPECIFIED',
    'MEASUREMENT_TYPE_KG',
    'MEASUREMENT_TYPE_GR',
    'MEASUREMENT_TYPE_ML',
    'MEASUREMENT_TYPE_L',
    'MEASUREMENT_TYPE_PCS',
    'MEASUREMENT_TYPE_PORTION',
  ])
  .catch('MEASUREMENT_TYPE_UNSPECIFIED');
export type MeasurementType = z.infer<typeof measurementTypeSchema>;

/** The merchant's accept/reject verdict on a pending order. */
export const merchantDecisionSchema = z
  .enum([
    'MERCHANT_DECISION_UNSPECIFIED',
    'MERCHANT_DECISION_ACCEPTED',
    'MERCHANT_DECISION_REJECTED',
  ])
  .catch('MERCHANT_DECISION_UNSPECIFIED');
export type MerchantDecision = z.infer<typeof merchantDecisionSchema>;

/**
 * How far an accepted order has progressed in the kitchen.
 *
 * `HANDED_TO_CUSTOMER` (v1.00.227) is the PICKUP counterpart of `PICKED_UP`:
 * the customer collected their самовывоз order at the counter, and it is the
 * only stage that moves a pickup order to `delivered` — a courier never touches
 * it, so `PICKED_UP` cannot.
 */
export const preparationStageSchema = z
  .enum([
    'PREPARATION_STAGE_UNSPECIFIED',
    'PREPARATION_STAGE_READY',
    'PREPARATION_STAGE_PICKED_UP',
    'PREPARATION_STAGE_HANDED_TO_CUSTOMER',
  ])
  .catch('PREPARATION_STAGE_UNSPECIFIED');
export type PreparationStage = z.infer<typeof preparationStageSchema>;

/**
 * `v1DeliveryStage` (v1.00.227) — the courier-tracking stage the CUSTOMER's
 * order screen renders, carried on `v1Order` itself. Independent of the
 * payment/kitchen axes: it answers "where is my food", not "is it paid".
 */
export const deliveryStageSchema = z
  .enum([
    'DELIVERY_STAGE_UNSPECIFIED',
    'DELIVERY_STAGE_AWAITING_MERCHANT',
    'DELIVERY_STAGE_MERCHANT_ACCEPTED',
    'DELIVERY_STAGE_COURIER_ASSIGNED',
    'DELIVERY_STAGE_PICKED_UP',
    'DELIVERY_STAGE_READY_FOR_PICKUP',
    'DELIVERY_STAGE_DELIVERED',
  ])
  .catch('DELIVERY_STAGE_UNSPECIFIED');
export type DeliveryStage = z.infer<typeof deliveryStageSchema>;

/** Weekday key on the order-counts histogram (`v1Weekday`). */
export const weekdaySchema = z
  .enum([
    'WEEKDAY_UNSPECIFIED',
    'WEEKDAY_MONDAY',
    'WEEKDAY_TUESDAY',
    'WEEKDAY_WEDNESDAY',
    'WEEKDAY_THURSDAY',
    'WEEKDAY_FRIDAY',
    'WEEKDAY_SATURDAY',
    'WEEKDAY_SUNDAY',
  ])
  .catch('WEEKDAY_UNSPECIFIED');
export type Weekday = z.infer<typeof weekdaySchema>;

/**
 * Weekday key on the working-hours schedule (`v1DayOfWeek`). Note the backend
 * uses a DIFFERENT spelling here than on `v1Weekday` above — bare `MONDAY`
 * rather than `WEEKDAY_MONDAY`. Both are modelled as-is rather than unified, so
 * a payload always round-trips to the endpoint it came from.
 */
export const dayOfWeekSchema = z
  .enum([
    'DAY_OF_WEEK_UNSPECIFIED',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY',
    'SUNDAY',
  ])
  .catch('DAY_OF_WEEK_UNSPECIFIED');
export type DayOfWeek = z.infer<typeof dayOfWeekSchema>;

// ── Value objects ────────────────────────────────────────────────────────────

/**
 * `{latitude, longitude}` geo point (`v1GeoPosition`).
 *
 * Both coordinates default to 0: protobuf-JSON omits zero-valued scalars, so a
 * point with no fix arrives as a bare `{}` rather than an absent key. A pickup
 * order, for instance, carries `"delivery_geo": {}` — marking the FIELD
 * optional does not help, because the key IS present. Requiring the coordinates
 * would fail the whole list payload.
 *
 * `0,0` is therefore "no position" here; callers that render a point must check
 * it (see `hasPosition`) rather than trusting the numbers.
 */
export const geoPositionSchema = z.object({
  latitude: z.number().default(0),
  longitude: z.number().default(0),
});
export type GeoPosition = z.infer<typeof geoPositionSchema>;

/** True when a geo point carries a real fix (not the `{}` / null-island default). */
export function hasPosition(position: GeoPosition | undefined): boolean {
  return position !== undefined && (position.latitude !== 0 || position.longitude !== 0);
}

/** A restaurant address: freeform line plus a geo point (`v1Address`). */
export const addressSchema = z.object({
  line: z.string().default(''),
  latitude: z.number().default(0),
  longitude: z.number().default(0),
});
export type Address = z.infer<typeof addressSchema>;

/** The restaurant's default daily window as `"HH:MM"` strings (`v1WorkingHours`). */
export const workingHoursSchema = z.object({
  open_time: z.string().default(''),
  close_time: z.string().default(''),
});
export type WorkingHours = z.infer<typeof workingHoursSchema>;

/** One weekday of the per-restaurant schedule (`v1DaySchedule`). */
export const dayScheduleSchema = z.object({
  order_from: z.string().optional(),
  order_to: z.string().optional(),
  day_of_week: dayOfWeekSchema,
  open_time: z.string().default(''),
  close_time: z.string().default(''),
  is_closed: z.boolean().default(false),
});
export type DaySchedule = z.infer<typeof dayScheduleSchema>;

// ── Pagination / gateway envelope ────────────────────────────────────────────
//
// The API gateway wraps every response in a uniform envelope, regardless of the
// per-service `v1*Response` proto message shown in the spec's `components`:
//
//   list endpoints  → { list: Item[], data: {}, meta }
//   single / write  → { data: <payload>, meta }
//
// So list items live in `list` (NOT the proto's `restaurants`/`products` key),
// and single-resource payloads live under a key on `data` (e.g.
// `{ restaurant: {...} }`). The `api/` layer models this with
// `listResponseSchema(item)` and `unwrapData` from `@noor/api`; consumers never
// see the envelope.

/**
 * Request pagination. List endpoints take limit/offset nested under a
 * `pagination.*` query key, flattened at the call site into `pagination.limit` /
 * `pagination.offset` (see `api/http.ts`).
 */
export const paginationParamsSchema = z.object({
  limit: z.number().int().positive().optional(),
  offset: z.number().int().nonnegative().optional(),
});
export type PaginationParams = z.infer<typeof paginationParamsSchema>;

/**
 * The `meta` block on a list envelope.
 *
 * The spec's `ListResponseMeta` also declares `page` and `page_count`, but the
 * gateway does NOT send them — the observed shape is `{ timestamp, total }`,
 * and endpoints such as working-hours and weekday-counts omit `total` as well.
 * Everything is therefore optional with a zero default; pagination is
 * limit/offset driven and `total` is the only figure a UI can trust.
 */
export const listResponseMetaSchema = z.object({
  total: z.number().int().nonnegative().default(0),
  page: z.number().int().nonnegative().optional(),
  page_count: z.number().int().nonnegative().optional(),
  timestamp: z.string().optional(),
});
export type ListResponseMeta = z.infer<typeof listResponseMetaSchema>;

/**
 * Build the list-envelope schema for a given item. Tolerates a missing/empty
 * `list` (→ `[]`) and a missing `meta` (→ zeroed). Callers get back the parsed
 * `{ list, meta }`.
 */
export function listResponseSchema<Item extends z.ZodType>(item: Item) {
  return z.object({
    list: z.array(item).default([]),
    meta: listResponseMetaSchema.default({ total: 0 }),
  });
}

/** A parsed list result: the rows plus pagination meta. */
export interface ListResult<T> {
  list: T[];
  meta: ListResponseMeta;
}
